import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import {
  createMarketingViewApi,
  deleteMarketingViewApi,
  getMarketingViewBaseSqlApi,
  updateMarketingViewApi,
  validateMarketingViewApi,
} from 'api/marketingApi';
import Card from 'components/card/Card';
import { DestructiveDialog } from 'components/Dialog';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import { CockpitUsersContext } from 'context/CockpitUsersContext';
import { useMarketingViews } from 'hooks/useMarketingViews';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  REQUIRED_MARKETING_VIEW_COLUMNS,
  marketingViewBqName,
  marketingViewDeleteMessage,
  marketingViewEditorLabel,
  marketingViewInUseMessage,
  parseMarketingViewBaseSql,
  parseMarketingViewUpsert,
  parseMarketingViewValidation,
} from './marketingViewSql';

const formatPreviewMonth = (month) => {
  if (!month) {
    return '';
  }
  if (month.year && month.month) {
    return `${month.year}-${String(month.month).padStart(2, '0')}`;
  }
  return String(month);
};

const CustomerMarketingViewsCard = ({ customerId }) => {
  const toast = useToast();
  const { getUserName } = useContext(CockpitUsersContext);
  const { views, loading, reload } = useMarketingViews(customerId);
  const [baseSql, setBaseSql] = useState('');
  const [templateSql, setTemplateSql] = useState('');
  const [requiredColumns, setRequiredColumns] = useState(REQUIRED_MARKETING_VIEW_COLUMNS);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [sql, setSql] = useState('');
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busyAction, setBusyAction] = useState('');
  const [viewToDelete, setViewToDelete] = useState(null);
  const isBusy = Boolean(busyAction);
  const inUseCloseRef = useRef();
  const viewToDeleteInUseMessage = marketingViewInUseMessage(viewToDelete);

  useEffect(() => {
    if (!customerId) {
      return;
    }
    getMarketingViewBaseSqlApi({ customerId })
      .then((res) => {
        const parsed = parseMarketingViewBaseSql(res);
        setBaseSql(parsed.baseSql);
        setTemplateSql(parsed.templateSql);
        setRequiredColumns(parsed.requiredColumns);
        setSql((current) => current || parsed.templateSql || parsed.baseSql);
      })
      .catch((error) => {
        toast({
          title: 'Failed to load default marketing SQL',
          description: error?.response?.data?.message || error?.message,
          status: 'error',
        });
      });
  }, [customerId, toast]);

  const resetEditor = (view) => {
    setSelectedId(view?.id || '');
    setName(view?.name || '');
    setSql(view?.sql || templateSql);
    setPreview([]);
    setErrors([]);
  };

  const onValidate = async () => {
    setBusyAction('validate');
    try {
      const res = parseMarketingViewValidation(await validateMarketingViewApi({ customerId, sql }));
      setErrors(res.errors);
      setPreview(res.preview);
      if (res.isValid) {
        toast({ title: 'SQL is valid', status: 'success' });
      } else {
        toast({ title: 'SQL is not valid', status: 'error' });
      }
    } catch (error) {
      toast({ title: 'Failed to validate marketing view', status: 'error' });
    } finally {
      setBusyAction('');
    }
  };

  const onSave = async () => {
    setBusyAction('save');
    try {
      const api = selectedId ? updateMarketingViewApi : createMarketingViewApi;
      const res = await api({
        customerId,
        viewId: selectedId,
        name,
        sql,
      });
      const parsed = parseMarketingViewUpsert(res);
      if (parsed.errors.length) {
        setErrors(parsed.errors);
        toast({ title: 'Could not save marketing view', status: 'error' });
        return;
      }
      toast({ title: 'Marketing view saved', status: 'success' });
      await reload();
      if (parsed.marketingView) {
        resetEditor({
          id: parsed.marketingView.id,
          name: parsed.marketingView.name,
          sql: parsed.marketingView.sql,
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to save marketing view',
        description: error?.response?.data?.message,
        status: 'error',
      });
    } finally {
      setBusyAction('');
    }
  };

  const onDelete = async (view) => {
    if (!view?.id) {
      return;
    }
    setBusyAction('delete');
    try {
      await deleteMarketingViewApi({ customerId, viewId: view.id });
      toast({ title: 'Marketing view deleted', status: 'success' });
      if (selectedId === view.id) {
        resetEditor(null);
      }
      await reload();
    } catch (error) {
      toast({
        title: 'Failed to delete marketing view',
        description: error?.response?.data?.message,
        status: 'error',
      });
    } finally {
      setBusyAction('');
    }
  };

  const onDuplicate = (view) => {
    resetEditor({
      id: '',
      name: `${view.name} copy`,
      sql: view.sql,
    });
  };

  const selectedView = (views || []).find((view) => view.id === selectedId);
  const isSelectedLocked = Boolean(selectedView?.isLocked);

  return (
    <OverlaySpinnerWrapper
      show={loading || isBusy}
      message={
        busyAction === 'validate'
          ? 'Validating SQL…'
          : busyAction === 'save'
            ? 'Saving…'
            : busyAction === 'delete'
              ? 'Deleting…'
              : undefined
      }
    >
      <Card mt={8}>
        <Heading variant={'sectionHeader'} borderColor={'secondaryGray.600'}>
          Marketing views
        </Heading>
        <Text mt={4} fontSize="sm" color="gray.600" maxW="720px">
          Write SQL over this customer&apos;s marketing data. Saved views can be selected on Sales,
          Marketing, deals, and installments. A view used by a funded installment is locked.
        </Text>
        <Box overflowX="auto" mt={6}>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>BQ view</Th>
                <Th>Last editor</Th>
                <Th>Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {(views || []).map((view) => (
                <Tr key={view.id} cursor="pointer" onClick={() => resetEditor(view)}>
                  <Td fontWeight={selectedId === view.id ? 800 : 500}>{view.name}</Td>
                  <Td fontFamily="mono" fontSize="xs">{marketingViewBqName(view.id)}</Td>
                  <Td>{marketingViewEditorLabel(view, getUserName)}</Td>
                  <Td>
                    {view.isLocked ? (
                      <Badge colorScheme="orange">Locked</Badge>
                    ) : view.isUsed ? (
                      <Badge colorScheme="purple">In use</Badge>
                    ) : (
                      'Editable'
                    )}
                  </Td>
                  <Td>
                    <Flex gap={2} justify="flex-end">
                      <Button size="xs" variant="outline" onClick={(event) => {
                        event.stopPropagation();
                        onDuplicate(view);
                      }}>
                        Duplicate
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        isDisabled={view.isLocked || isBusy}
                        onClick={(event) => {
                          event.stopPropagation();
                          setViewToDelete(view);
                        }}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        <Flex mt={6} gap={6} direction={{ base: 'column', xl: 'row' }}>
          <Box flex={1}>
            <Flex justify="space-between" mb={3} align="flex-start">
              <Box>
                <Text fontWeight={700}>{selectedId ? 'Edit view' : 'New view'}</Text>
                {selectedId && (
                  <Text mt={1} fontFamily="mono" fontSize="xs" color="gray.600">
                    {marketingViewBqName(selectedId)}
                  </Text>
                )}
              </Box>
              <Button size="sm" variant="ghost" onClick={() => resetEditor(null)}>
                New
              </Button>
            </Flex>
            {isSelectedLocked && (
              <Text mb={3} fontSize="sm" color="orange.600">
                This view is locked because a funded installment uses it. Duplicate it to make changes.
              </Text>
            )}
            <Input
              mb={3}
              placeholder="View name"
              value={name}
              isDisabled={isSelectedLocked}
              onChange={(event) => setName(event.target.value)}
            />
            <Text mb={2} fontSize="sm" fontWeight={600}>
              Keep these SELECT columns
            </Text>
            <Flex mb={3} gap={2} wrap="wrap">
              {requiredColumns.map((column) => (
                <Badge key={`${column.name}-${column.type}`} variant="subtle" colorScheme="purple">
                  {column.name} {column.type}
                </Badge>
              ))}
            </Flex>
            <Textarea
              fontFamily="mono"
              fontSize="sm"
              minH="220px"
              value={sql}
              isDisabled={isSelectedLocked}
              onChange={(event) => setSql(event.target.value)}
            />
            <Flex mt={3} gap={3}>
              <Button
                variant="outline"
                onClick={onValidate}
                isDisabled={!sql || isBusy}
                isLoading={busyAction === 'validate'}
                loadingText="Validating"
              >
                Validate
              </Button>
              <Button
                variant="brand"
                onClick={onSave}
                isDisabled={isSelectedLocked || !name || !sql || isBusy}
                isLoading={busyAction === 'save'}
                loadingText="Saving"
              >
                Save
              </Button>
            </Flex>
            {errors.length > 0 && (
              <Box mt={3}>
                {errors.map((error) => (
                  <Text key={error} color="red.500" fontSize="sm">
                    {error}
                  </Text>
                ))}
              </Box>
            )}
          </Box>
          <Box flex={1}>
            <Text fontWeight={700} mb={3}>
              Default marketing SQL
            </Text>
            <Textarea fontFamily="mono" fontSize="sm" minH="220px" value={baseSql} isReadOnly />
          </Box>
        </Flex>
        {preview.length > 0 && (
          <Box mt={6} overflowX="auto">
            <Text fontWeight={700} mb={3}>
              Preview (last 6 months)
            </Text>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Month</Th>
                  <Th>Source</Th>
                  <Th isNumeric>Default spend</Th>
                  <Th isNumeric>View spend</Th>
                </Tr>
              </Thead>
              <Tbody>
                {preview.map((row, index) => (
                  <Tr key={`${formatPreviewMonth(row.month)}-${row.source}-${index}`}>
                    <Td>{formatPreviewMonth(row.month)}</Td>
                    <Td>{row.source}</Td>
                    <Td isNumeric>{row.defaultSpend}</Td>
                    <Td isNumeric>{row.viewSpend}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>
      <DestructiveDialog
        show={Boolean(viewToDelete) && !viewToDeleteInUseMessage}
        header="Delete marketing view"
        body={marketingViewDeleteMessage(viewToDelete)}
        onDelete={() => onDelete(viewToDelete)}
        onDialogClose={() => setViewToDelete(null)}
      />
      <AlertDialog
        isOpen={Boolean(viewToDelete) && Boolean(viewToDeleteInUseMessage)}
        leastDestructiveRef={inUseCloseRef}
        onClose={() => setViewToDelete(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cannot delete marketing view
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm" color="gray.main">
              {viewToDeleteInUseMessage}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={inUseCloseRef} onClick={() => setViewToDelete(null)}>
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </OverlaySpinnerWrapper>
  );
};

CustomerMarketingViewsCard.propTypes = {
  customerId: PropTypes.string,
};

export default CustomerMarketingViewsCard;
